// src/pages/Admin.jsx
import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import AdminPanel from '../components/AdminPanel'
import { setAuthToken, verifyCredentials, getAuthToken, clearAuthToken } from '../utils/auth'
import { saveHuntCache, initOfflineDB } from '../utils/offline'
import { fetchHunt } from '../utils/kv'

export default function Admin() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [isLoginMode, setIsLoginMode] = useState(!getAuthToken())
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState(null)
  const [loggingIn, setLoggingIn] = useState(false)
  const [hunt, setHunt] = useState(null)

  // Initialize IndexedDB and load hunt on mount
  useEffect(() => {
    async function init() {
      await initOfflineDB()
      const huntData = await fetchHunt()
      if (huntData) {
        setHunt(huntData)
      }
    }
    init()
  }, [])

  async function handleLogin(e) {
    e.preventDefault()
    setLoggingIn(true)
    setError(null)

    const isValid = await verifyCredentials(username, password)

    if (isValid) {
      const credentials = btoa(`${username}:${password}`)
      setAuthToken(`Basic ${credentials}`)
      setIsLoginMode(false)
      setUsername('')
      setPassword('')
    } else {
      setError(t('admin.invalidCredentials'))
    }

    setLoggingIn(false)
  }

  async function handleSaveHunt(huntData) {
    try {
      // Save locally
      await saveHuntCache(huntData)

      // Also try to save to Cloudflare KV
      const response = await fetch('/api/hunt', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': getAuthToken()
        },
        body: JSON.stringify(huntData)
      })

      if (response.ok) {
        console.log('Hunt saved to Cloudflare KV and local cache:', huntData)
      } else {
        console.warn('Failed to save to KV, but saved locally:', huntData)
      }
    } catch (err) {
      console.error('Error saving hunt:', err)
    }
  }

  function handleLogout() {
    clearAuthToken()
    setIsLoginMode(true)
    navigate('/admin')
  }

  if (isLoginMode) {
    return (
      <div style={styles.loginContainer}>
        <div style={styles.loginBox}>
          <h1>{t('admin.login')}</h1>
          <form onSubmit={handleLogin}>
            <input
              type="text"
              placeholder={t('admin.username')}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              disabled={loggingIn}
              style={styles.input}
            />
            <input
              type="password"
              placeholder={t('admin.password')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loggingIn}
              style={styles.input}
            />

            {error && (
              <p style={{ color: 'red', fontSize: '14px', textAlign: 'center' }}>
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={loggingIn}
              style={{
                ...styles.button,
                width: '100%',
                opacity: loggingIn ? 0.6 : 1,
                cursor: loggingIn ? 'not-allowed' : 'pointer'
              }}
            >
              {loggingIn ? t('admin.loggingIn') : t('admin.loginButton')}
            </button>
          </form>
        </div>
      </div>
    )
  }

  return (
    <div style={styles.adminContainer}>
      <button onClick={handleLogout} style={styles.logoutButton}>
        {t('admin.logout')}
      </button>

      <AdminPanel onSave={handleSaveHunt} initialHunt={hunt} />
    </div>
  )
}

const styles = {
  loginContainer: {
    width: '100%',
    height: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
  },
  loginBox: {
    background: 'white',
    padding: '40px',
    borderRadius: '8px',
    maxWidth: '400px',
    width: '90%',
    boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
  },
  input: {
    width: '100%',
    padding: '12px',
    marginBottom: '15px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '16px',
    boxSizing: 'border-box'
  },
  button: {
    padding: '12px',
    background: '#007bff',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '16px',
    cursor: 'pointer'
  },
  adminContainer: {
    position: 'relative',
    minHeight: '100vh',
    background: '#f5f5f5'
  },
  logoutButton: {
    position: 'absolute',
    top: '20px',
    right: '20px',
    padding: '8px 16px',
    background: '#dc3545',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    zIndex: 100
  }
}
